<template>
  <div :ref="mapId" class="mt-1 map elevation-3 rounded-3" />
</template>

<script>
import gmaps from "@/gmaps.js"
import Helpers from "@/helpers.js"
import { mapState } from "vuex"

export default {
  name: "Map",
  data: () => {
    return {
      markerImageUrl: "https://www.jonathan-creative.com/clients/caseblocks/map-marker.png",
      mapStyleUrl: "https://www.jonathan-creative.com/clients/caseblocks/m1.png",
      map: null,
      markers: [],
      maxMarkers: 1,
      selectedZoom: 15,
      defaultZoom: 6
    }
  },
  props: {
    name: {
      type: String,
      default: "unnamed"
    },
    row: {
      type: Number
    },
    address: {
      type: Object
    },
    readonly: {
      type: Boolean,
      default: false
    },
    defaultLocation: {
      type: String,
      default: "europe"
    },
    gmapHelpers: {
      type: Object,
      default: function() {
        return gmaps
      }
    }
  },
  watch: {
    address() {
      this.updateLocation()
    }
  },
  mounted() {
    if (!this.map) {
      this.init()
    }
  },
  computed: {
    ...mapState({
      currentUser: state => state.currentUser
    }),
    apiKey() {
      try {
        const attrs = JSON.parse(this.currentUser.account.lifeCycleCallbacks)
        const apiKey = attrs.params.google.places.apiKey
        return apiKey
      } catch (error) {
        console.log("API Key not found, using default")
        return "AIzaSyClXhrOdY3Pw9lLHAMojsVloOrowThgGek"
      } 
    },
    mapId() {
      const title = Helpers.formatSnakeCase(this.name)
      return `${title}${this.row ? "_" + this.row : ""}_map`
    }
  },
  methods: {
    async init() {
      try {
        await this.gmapHelpers.init(this.apiKey)
        this.map = this.gmapHelpers.getMap(this.$refs[this.mapId])
        await this.updateLocation()

        const bucketMap = this
        if (!this.readonly) {
          this.map.addListener('click', function(e) {
            bucketMap.addMarker(e.latLng)
          })
        }
      } catch (error) {
        console.error(error)
      }
    },
    async updateLocation() {
      let initialAddress = this.defaultLocation ? this.defaultLocation : "europe"
      if (this.address) {
        initialAddress = this.address.name + " " + this.address.country + ", " + this.address.postal_code
        const latLng = this.gmapHelpers.getLatLng(this.address.latitude, this.address.longitude)
        await this.addMarker(latLng)
        this.map.setCenter(latLng)
        this.map.setZoom(this.selectedZoom)
      } else {
        const result = await this.gmapHelpers.geocode({ address: initialAddress })
        if (result && result.geometry) {
          this.map.setCenter(result.geometry.location)
          this.map.fitBounds(result.geometry.viewport)
          this.map.setZoom(this.defaultZoom)
        }
      }
    },
    async addMarker(latLng) {
      if (this.markers.length < this.maxMarkers) {
        const marker = this.gmapHelpers.getMarker({
          map: this.map,
          position: latLng,
          draggable: !this.readonly
        })
        const bucketMap = this
        marker.addListener("dragend", function(e) {
          bucketMap.updatePosition(marker)
        })
        this.markers.push(marker)
        await this.updatePosition(marker)
      } else {
        this.markers[this.maxMarkers - 1].setPosition(latLng)
        await this.updatePosition(this.markers[this.maxMarkers - 1])
      }
    },
    async updatePosition(marker) {
      this.map.panTo(marker.position)
      if (this.map.getZoom() < this.selectedZoom - 1) {
        this.map.setZoom(this.selectedZoom)
      }
      const result = await this.gmapHelpers.geocode({ location: marker.position })
      if (result && result.address_components) {
        const address = {}
        let name = ""
        for (const part of result.address_components) {
          for (const type of part.types) {
            address[type] = part.long_name
            if (type === "street_number") { name += part.long_name + " " }
            if (type === "route") { name += part.short_name }
          }
          if (address.point_of_interest) {
            address.name = address.point_of_interest
          } else {
            if (name) {
              address.name = name
            } else {
              if (address.postal_town) { address.name = address.postal_town }
            }
          }
        }
        if (!this.address || address.postal_code !== this.address.postal_code) {
          address.latitude = marker.position.lat()
          address.longitude = marker.position.lng()
          this.$emit('update-item', { data: address })
        }
      }
    }
  }
}
</script>

<style>
.map {
  width: 100%;
  height: 500px;
}

.map-popup {
  background: white;
  position: relative;
  padding: 15px 30px 15px 70px;
}

.coloured-bg {
  width: 40px !important;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border-top-left-radius: 3px;
  border-bottom-left-radius: 3px;
  background: #7eaab5; /* Old browsers */
  background: -moz-linear-gradient(
    top,
    #7eaab5 0%,
    #468595 100%
  ); /* FF3.6-15 */
  background: -webkit-linear-gradient(
    top,
    #7eaab5 0%,
    #468595 100%
  ); /* Chrome10-25,Safari5.1-6 */
  background: linear-gradient(
    to bottom,
    #7eaab5 0%,
    #468595 100%
  ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
}

/*style the box*/
.gm-style .gm-style-iw {
  top: 0 !important;
  left: 0 !important;
  width: 250px !important;
  padding: 0px;
  display: block !important;
  border-radius: 3px;
}

.gm-style-iw-d {
  overflow: auto !important;
}

/*style the p tag*/
.gm-style .gm-style-iw #google-popup p {
  padding: 10px;
}
</style>
